import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { videosService, coursesService } from '../services/api';
import { Video, Course, VideoStreamInfo } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/imageUtils';
import { getCoursePathSegment } from '../utils/coursePath';
import {
  buildCourseVideoPlaybackUrl,
  playbackUrlHasStreamToken,
} from '../utils/videoPlaybackUrl';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  BookOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Gauge,
  LayoutDashboard,
  ListVideo,
  Maximize,
  Minimize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from 'lucide-react';

import './video-player-native.css';

const toFaDigits = (value: string | number) =>
  String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

/** فقط برای دیباگ محلی؛ در production حتماً false بماند (استریم تست بدون احراز هویت است) */
const USE_STREAM_TEST =
  typeof process !== 'undefined' &&
  process.env.REACT_APP_VIDEO_STREAM_USE_TEST === 'true';

const QUALITY_OPTIONS = [
  { value: 'auto', label: 'خودکار' },
  { value: '1080p', label: '۱۰۸۰p' },
  { value: '720p', label: '۷۲۰p' },
  { value: '480p', label: '۴۸۰p' },
];

function chapterTitleForVideo(
  course: Course | null,
  videoId: string | undefined,
): string | null {
  if (!course?.chapters?.length || !videoId) return null;
  const chapters = [...course.chapters].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );
  for (const ch of chapters) {
    const lessons = [...(ch.lessons ?? [])].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
    for (const lesson of lessons) {
      if (lesson.video?.id === videoId) return ch.title;
    }
  }
  return null;
}

function formatNativePlaybackError(videoEl: HTMLVideoElement): string {
  const code = videoEl.error?.code;
  const byCode: Record<number, string> = {
    1: 'پخش توسط کاربر یا مرورگر قطع شد.',
    2: 'اتصال پخش قطع شد؛ صفحه را دوباره بارگذاری کنید.',
    3: 'فایل نامعتبر یا کدک پشتیبانی‌نشده.',
    4: 'این ویدیو در این مرورگر پخش نشد؛ صفحه را دوباره بارگذاری کنید یا مرورگر دیگری را امتحان کنید.',
  };
  if (typeof code === 'number' && byCode[code]) return byCode[code];
  return 'پخش انجام نشد. این صفحه از پخش‌کنندهٔ بومی مرورگر استفاده می‌کند (پایدارتر از Video.js برای استریم JWT).';
}

const VideoPlayerPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  /** پخش بومی؛ برای همین مسیر JWT+Range با Video.js روی بعضی دستگاه‌ها سیاه می‌ماند */
  const nativeVideoRef = useRef<HTMLVideoElement | null>(null);
  const playerFrameRef = useRef<HTMLDivElement | null>(null);

  const [videoInfo, setVideoInfo] = useState<VideoStreamInfo | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [courseVideos, setCourseVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [selectedQuality, setSelectedQuality] = useState('auto');
  const [resumeAtSec, setResumeAtSec] = useState<number | null>(null);
  const [playbackError, setPlaybackError] = useState('');
  const [resumePlayingAfterRefresh, setResumePlayingAfterRefresh] = useState(false);

  // --- کنترل‌های سفارشی پخش (روی همان <video> بومی؛ منطق استریم/توکن/کیفیت دست‌نخورده) ---
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedPct, setBufferedPct] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimer = useRef<number | undefined>(undefined);
  const recoveryTimesRef = useRef<number[]>([]);
  /** مدت واقعی از هدر فایل؛ بعضی مرورگرها بعد از قطع اتصال duration را کوتاه می‌کنند */
  const metadataDurationRef = useRef(0);

  const buildStreamUrl = useCallback(
    (
      quality: string,
      currentVideoId: string,
      info: Pick<VideoStreamInfo, 'streamUrl'> | null,
    ) =>
      buildCourseVideoPlaybackUrl(currentVideoId, info, quality, {
        streamTest: USE_STREAM_TEST,
      }),
    [],
  );

  useEffect(() => {
    setResumeAtSec(null);
    setPlaybackError('');
    setResumePlayingAfterRefresh(false);
    recoveryTimesRef.current = [];
    metadataDurationRef.current = 0;
  }, [videoId]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (videoId) {
      void (async () => {
        try {
          setLoading(true);
          setError('');

          const streamData = await videosService.getVideoStreamUrl(videoId);
          setVideoInfo(streamData);

          const url = buildStreamUrl('auto', videoId, streamData);
          const tokenOk =
            USE_STREAM_TEST || playbackUrlHasStreamToken(url || '');
          if (!url.trim() || !tokenOk) {
            setError(
              'توکن ورود نامعتبر است یا آدرس پخش بدون پارامتر احراز بود. یک‌بار از حساب خارج و دوباره وارد شوید؛ اگر بین localhost و 127.0.0.1 عوض می‌کنید، همیشه از یکی برای سایت استفاده کنید.',
            );
          } else {
            setPlaybackError('');
            setVideoUrl(url);
          }

          if (streamData.courseId) {
            const [courseData, videosData] = await Promise.all([
              coursesService.getById(streamData.courseId),
              videosService.getMyVideos(),
            ]);

            setCourse(courseData);

            const courseVideosList = videosData
              .filter((v) => v.courseId === streamData.courseId)
              .sort((a, b) => a.order - b.order);
            setCourseVideos(courseVideosList);
          }
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || 'خطا در بارگذاری ویدیو';
          setError(msg);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [
    authLoading,
    buildStreamUrl,
    navigate,
    user,
    videoId,
  ]);

  // توکن پخش داخل آدرس است و <video> تا آخر همان آدرس را می‌خواهد؛ اگر وسط کار رد شود
  // (مثلاً بعد از مکث طولانی) آدرس تازه می‌گیریم و از همان ثانیه ادامه می‌دهیم.
  // سقف ۳ بار در ۲ دقیقه تا خطای واقعی (کدک/فایل) حلقهٔ بی‌پایان نسازد.
  const recoverStream = useCallback(
    async (resumePlaying: boolean): Promise<boolean> => {
      const el = nativeVideoRef.current;
      if (USE_STREAM_TEST || !videoId || !el) return false;
      const now = Date.now();
      recoveryTimesRef.current = recoveryTimesRef.current.filter((t) => now - t < 120_000);
      if (recoveryTimesRef.current.length >= 3) return false;
      recoveryTimesRef.current.push(now);

      const currentTime = el.currentTime || 0;
      try {
        const refreshed = await videosService.refreshPlaybackToken(videoId);
        const next = buildStreamUrl(selectedQuality, videoId, {
          streamUrl: refreshed.streamUrl,
        });
        if (!next) return false;
        setResumeAtSec(currentTime > 0.35 ? currentTime : null);
        setResumePlayingAfterRefresh(resumePlaying);
        setPlaybackError('');
        setVideoUrl(next);
        return true;
      } catch {
        return false;
      }
    },
    [buildStreamUrl, selectedQuality, videoId],
  );

  useEffect(() => {
    const root = playerFrameRef.current;
    if (!root) return;

    const hideIdmBadges = () => {
      const candidates = Array.from(
        document.querySelectorAll<HTMLElement | HTMLIFrameElement>(
          [
            '[id*="idm"]',
            '[class*="idm"]',
            'iframe[src*="idm"]',
            'iframe[id*="idm"]',
            'iframe[class*="idm"]',
          ].join(','),
        ),
      );

      candidates.forEach((el) => {
        const text = (el.textContent || '').toLowerCase();
        const id = (el.id || '').toLowerCase();
        const className = (typeof el.className === 'string' ? el.className : '').toLowerCase();
        const title = String((el as HTMLElement).getAttribute?.('title') || '').toLowerCase();
        const looksLikeDownloadBadge =
          text.includes('download this video') ||
          title.includes('download this video') ||
          id.includes('idm') ||
          className.includes('idm');

        if (!looksLikeDownloadBadge) return;
        const rect = el.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const overlapsPlayer =
          rect.width > 0 &&
          rect.height > 0 &&
          rect.left < rootRect.right &&
          rect.right > rootRect.left &&
          rect.top < rootRect.bottom &&
          rect.bottom > rootRect.top;

        if (overlapsPlayer) {
          (el as HTMLElement).style.setProperty('display', 'none', 'important');
          (el as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
          (el as HTMLElement).style.setProperty('pointer-events', 'none', 'important');
        }
      });
    };

    hideIdmBadges();
    const observer = new MutationObserver(() => hideIdmBadges());
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = window.setInterval(hideIdmBadges, 1500);

    return () => {
      observer.disconnect();
      window.clearInterval(timer);
    };
  }, [videoUrl]);

  const handleQualityChange = (quality: string) => {
    if (!videoId) return;

    const t = nativeVideoRef.current?.currentTime ?? 0;
    if (t > 0.35) setResumeAtSec(t);
    else setResumeAtSec(null);

    setSelectedQuality(quality);
    setPlaybackError('');
    const next = buildStreamUrl(quality, videoId, videoInfo);
    setVideoUrl(next || '');
  };

  const handleVideoSelect = (selectedVideoId: string) => {
    navigate(`/courses/${courseId}/videos/${selectedVideoId}`);
  };

  const getCurrentVideoIndex = () =>
    courseVideos.findIndex((v) => v.id === videoId);

  const goToNextVideo = () => {
    const currentIndex = getCurrentVideoIndex();
    if (currentIndex < courseVideos.length - 1) {
      handleVideoSelect(courseVideos[currentIndex + 1].id);
    }
  };

  const goToPreviousVideo = () => {
    const currentIndex = getCurrentVideoIndex();
    if (currentIndex > 0) {
      handleVideoSelect(courseVideos[currentIndex - 1].id);
    }
  };

  const onResumeApplied = useCallback(() => {
    setResumeAtSec(null);
  }, []);

  const revealControls = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimer.current) window.clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = window.setTimeout(() => setShowControls(false), 2800);
  }, []);

  const togglePlay = useCallback(() => {
    const v = nativeVideoRef.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => undefined);
    else v.pause();
  }, []);

  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  useEffect(
    () => () => {
      if (hideControlsTimer.current) window.clearTimeout(hideControlsTimer.current);
    },
    [],
  );

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#E9EDF7] px-4"
        dir="rtl"
      >
        <div className="max-w-md rounded-2xl border border-red-200 bg-white px-6 py-5 text-center shadow-[0_12px_28px_rgba(220,38,38,0.08)]">
          <h2 className="mb-2 text-lg font-extrabold text-[#B91C1C]">خطا در دسترسی به ویدیو</h2>
          <p className="mb-4 text-sm leading-relaxed text-[#475569]">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-xl bg-[#1C3B9B] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#153088]"
          >
            بازگشت به داشبورد
          </button>
        </div>
      </div>
    );
  }

  if (!videoInfo) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#E9EDF7] px-4"
        dir="rtl"
      >
        <div className="rounded-2xl border border-[#DDE5FF] bg-white p-8 text-center shadow-[0_10px_30px_rgba(28,59,155,0.08)]">
          <h2 className="mb-4 text-xl font-black text-[#0F172A]">ویدیو یافت نشد</h2>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-xl bg-[#1C3B9B] px-6 py-2.5 font-bold text-white transition hover:bg-[#153088]"
          >
            بازگشت به داشبورد
          </button>
        </div>
      </div>
    );
  }

  const currentIndex = getCurrentVideoIndex();
  const hasNext = currentIndex < courseVideos.length - 1;
  const hasPrevious = currentIndex > 0;

  const progressPct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const controlsVisible = showControls || !isPlaying || isBuffering;
  const volPct = (isMuted ? 0 : volume) * 100;

  const fmtTime = (s: number) => {
    const t = Number.isFinite(s) && s > 0 ? s : 0;
    const m = Math.floor(t / 60);
    const sec = Math.floor(t % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const onSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = nativeVideoRef.current;
    if (!v || !(duration > 0)) return;
    const t = (parseFloat(e.target.value) / 100) * duration;
    v.currentTime = t;
    setCurrentTime(t);
  };

  const toggleMute = () => {
    const v = nativeVideoRef.current;
    if (v) v.muted = !v.muted;
  };

  const onVolumeSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = nativeVideoRef.current;
    if (!v) return;
    const val = parseFloat(e.target.value);
    v.volume = val;
    v.muted = val === 0;
  };

  const cyclePlaybackRate = () => {
    const rates = [1, 1.25, 1.5, 2, 0.75];
    const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
    const v = nativeVideoRef.current;
    if (v) v.playbackRate = next;
    setPlaybackRate(next);
  };

  const toggleFullscreen = () => {
    const el = playerFrameRef.current as (HTMLDivElement & { requestFullscreen?: () => Promise<void> }) | null;
    const v = nativeVideoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
      return;
    }
    if (el?.requestFullscreen) void el.requestFullscreen();
    else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  const seekBg = `linear-gradient(to right, #7c93ff 0%, #7c93ff ${progressPct}%, rgba(255,255,255,0.32) ${progressPct}%, rgba(255,255,255,0.32) ${bufferedPct}%, rgba(255,255,255,0.18) ${bufferedPct}%, rgba(255,255,255,0.18) 100%)`;
  const volBg = `linear-gradient(to right, #a5b4fc 0%, #a5b4fc ${volPct}%, rgba(255,255,255,0.25) ${volPct}%, rgba(255,255,255,0.25) 100%)`;

  const poster = videoInfo.thumbnail
    ? getImageUrl(videoInfo.thumbnail) || undefined
    : undefined;

  const courseHref = course ? `/courses/${getCoursePathSegment(course)}` : '/courses';
  const chapterTitle = chapterTitleForVideo(course, videoId);
  const courseCoverUrl =
    course?.thumbnail != null && String(course.thumbnail).trim()
      ? getImageUrl(course.thumbnail) ?? null
      : null;

  return (
    <div
      className="relative min-h-[100dvh] min-h-screen bg-[#E9EDF7] pb-[max(5.5rem,env(safe-area-inset-bottom,0px))] pt-3 text-[#0F172A] sm:pb-24 sm:pt-8"
      dir="rtl"
    >
      <style>{`
        .fa-list-eq { height: 14px; transform-origin: bottom; animation: fa-list-eq-kf 0.85s ease-in-out infinite; }
        @keyframes fa-list-eq-kf { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
        .fa-media-scroll { scrollbar-width: thin; scrollbar-color: #c3cbe0 transparent; }
        .fa-media-scroll::-webkit-scrollbar { width: 6px; }
        .fa-media-scroll::-webkit-scrollbar-track { background: transparent; margin: 6px 0; }
        .fa-media-scroll::-webkit-scrollbar-thumb { background-color: #c3cbe0; border-radius: 9999px; }
        .fa-media-scroll::-webkit-scrollbar-thumb:hover { background-color: #9aa6c4; }
        .fa-video-range, .fa-video-vol { -webkit-appearance: none; appearance: none; border-radius: 9999px; cursor: pointer; outline: none; }
        .fa-video-range { height: 5px; }
        .fa-video-vol { height: 4px; }
        .fa-video-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 13px; height: 13px; border-radius: 9999px; background: #fff; box-shadow: 0 0 0 4px rgba(99,102,241,0.35), 0 2px 6px rgba(0,0,0,0.5); transition: transform .15s ease; }
        .fa-video-range:hover::-webkit-slider-thumb { transform: scale(1.15); }
        .fa-video-range::-moz-range-thumb { width: 13px; height: 13px; border: none; border-radius: 9999px; background: #fff; box-shadow: 0 0 0 4px rgba(99,102,241,0.35), 0 2px 6px rgba(0,0,0,0.5); }
        .fa-video-vol::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 11px; height: 11px; border-radius: 9999px; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.5); }
        .fa-video-vol::-moz-range-thumb { width: 11px; height: 11px; border: none; border-radius: 9999px; background: #fff; }
        .fa-fs:fullscreen { width: 100vw; height: 100vh; max-width: none; border-radius: 0; }
        .fa-fs:-webkit-full-screen { width: 100vw; height: 100vh; max-width: none; border-radius: 0; }
      `}</style>
      {USE_STREAM_TEST ? (
        <div className="mb-3 border-y border-amber-200 bg-amber-50 px-3 py-1.5 text-center text-[10px] font-semibold text-amber-900 sm:mb-4 sm:px-4 sm:py-2 sm:text-[11px]">
          دیباگ: استریم بدون محدودیت فعال است — در پروداکشن{' '}
          <code className="rounded bg-white/80 px-1">REACT_APP_VIDEO_STREAM_USE_TEST</code> را خاموش کنید.
        </div>
      ) : null}

      <div className="mx-auto max-w-7xl px-[max(0.75rem,env(safe-area-inset-right,0px),env(safe-area-inset-left,0px))] sm:px-6 lg:px-8">
        <nav
          aria-label="مسیر صفحه"
          className="mb-3 flex snap-x snap-mandatory flex-nowrap items-center gap-x-1.5 overflow-x-auto pb-0.5 text-[10px] text-[#64748B] [-ms-overflow-style:none] [scrollbar-width:none] sm:mb-4 sm:flex-wrap sm:gap-2 sm:text-sm [&::-webkit-scrollbar]:hidden"
        >
          <span
            role="presentation"
            className="snap-start shrink-0 cursor-pointer transition hover:text-[#1C3B9B]"
            onClick={() => navigate('/')}
          >
            خانه
          </span>
          <span className="shrink-0 text-slate-300">/</span>
          <span
            role="presentation"
            className="snap-start shrink-0 cursor-pointer transition hover:text-[#1C3B9B]"
            onClick={() => navigate('/courses')}
          >
            دوره‌ها
          </span>
          {course ? (
            <>
              <span className="shrink-0 text-slate-300">/</span>
              <span
                role="presentation"
                className="max-w-[45vw] cursor-pointer truncate font-semibold text-[#0F172A] transition hover:text-[#1C3B9B] sm:max-w-none"
                onClick={() => navigate(courseHref)}
                title={course.title}
              >
                {course.title}
              </span>
            </>
          ) : null}
          <span className="shrink-0 text-slate-300">/</span>
          <span className="snap-start shrink-0 font-semibold text-[#0F172A]">پخش جلسه</span>
        </nav>

        <div className="-mx-3 mb-3 w-[calc(100%+1.5rem)] rounded-[1.25rem] border border-slate-300/60 bg-transparent px-2.5 py-2.5 sm:mx-0 sm:mb-6 sm:w-auto sm:rounded-[1.5rem] sm:px-5 sm:py-5 md:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              {courseCoverUrl ? (
                <div className="relative shrink-0 overflow-hidden rounded-2xl ring-1 ring-slate-300/70 sm:rounded-[1.125rem]">
                  <img
                    src={courseCoverUrl}
                    alt={course ? `کاور دوره ${course.title}` : 'کاور دوره'}
                    className="h-[4.25rem] w-[4.25rem] object-cover sm:h-[5.75rem] sm:w-[5.75rem]"
                    loading="lazy"
                  />
                </div>
              ) : null}
              <div className="min-w-0 flex-1 text-right">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1C3B9B]/10 px-2.5 py-1 text-[9px] font-bold tracking-wide text-[#1C3B9B] ring-1 ring-inset ring-[#1C3B9B]/20 sm:text-[11px]">
                  <Play className="size-3 sm:size-3.5" strokeWidth={2.5} aria-hidden />
                  {chapterTitle ? `فصل: ${chapterTitle}` : 'پخش ویدیوی دوره'}
                </span>
                <h1 className="mt-1.5 text-[1.05rem] font-black leading-snug tracking-tight text-slate-900 sm:text-2xl md:text-[1.7rem]">
                  {videoInfo.title}
                </h1>
                {course && (
                  <p className="mt-0.5 truncate text-[11px] text-slate-600 sm:mt-1 sm:text-sm">{course.title}</p>
                )}
              </div>
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end sm:gap-2.5">
              <button
                type="button"
                onClick={() => navigate(courseHref)}
                className="group inline-flex min-h-[42px] items-center justify-center gap-2 rounded-2xl bg-[#1C3B9B] px-3.5 py-2.5 text-[11px] font-black tracking-tight text-white transition hover:bg-[#173183] active:scale-[0.98] sm:min-h-[48px] sm:gap-2.5 sm:px-6 sm:text-sm"
              >
                <BookOpen className="size-4 shrink-0 text-white transition-transform group-hover:-translate-x-0.5 sm:size-[1.15rem]" strokeWidth={2.5} aria-hidden />
                <span className="leading-snug">بازگشت به دوره</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-transparent px-3.5 py-2.5 text-[11px] font-black tracking-tight text-slate-700 transition hover:border-[#1C3B9B]/50 hover:text-[#1C3B9B] active:scale-[0.98] sm:min-h-[48px] sm:gap-2.5 sm:px-6 sm:text-sm [&_svg]:text-slate-500 hover:[&_svg]:text-[#1C3B9B]"
              >
                <LayoutDashboard className="size-4 shrink-0 sm:size-[1.15rem]" strokeWidth={2.5} aria-hidden />
                <span className="leading-snug">داشبورد</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-[minmax(0,1fr)_24rem] lg:gap-8">
          <div className="min-w-0 w-full max-w-full space-y-4 sm:space-y-5">
            <div
              dir="rtl"
              className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-gradient-to-br from-[#212c52] via-[#273263] to-[#1e284b] shadow-[0_30px_70px_-34px_rgba(30,40,80,0.85)] ring-1 ring-white/[0.06]"
            >
              {playbackError ? (
                <div className="border-b border-red-500/40 bg-red-900/85 px-3 py-2 text-center text-[10px] font-bold leading-relaxed text-red-100 sm:px-4 sm:py-2.5 sm:text-xs">
                  {playbackError}
                </div>
              ) : null}

              <div className="border-b border-white/[0.08] bg-white/[0.03] px-3 pb-2.5 pt-2 sm:flex sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5 sm:py-3.5 md:py-4">
                <div className="flex min-w-0 items-start gap-2 text-right sm:items-center sm:gap-3">
                  <span className="mt-px flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2f5ae0] text-[9px] font-black tracking-[0.12em] text-white ring-1 ring-white/20 sm:h-11 sm:w-11 sm:rounded-[0.875rem] sm:text-[10px]">
                    VOD
                  </span>
                  <div className="min-w-0 flex-1">
                    {chapterTitle ? (
                      <p className="text-[9px] font-bold tracking-wide text-indigo-200 sm:text-[11px]">
                        فصل: {chapterTitle}
                      </p>
                    ) : (
                      <p className="text-[9px] font-semibold tracking-wide text-slate-300 sm:text-[11px]">
                        پخش اختصاصی
                      </p>
                    )}
                    <p className="truncate text-[12px] font-bold leading-snug text-white sm:text-sm">
                      {videoInfo.title}
                    </p>
                    {course?.title ? (
                      <p className="mt-px truncate text-[10px] font-medium leading-snug text-slate-300 sm:text-[11px]">
                        {course.title}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-2.5 flex w-full min-w-0 items-center gap-2 border-t border-white/[0.08] pt-2.5 sm:mt-0 sm:w-auto sm:shrink-0 sm:border-t-0 sm:pt-0">
                  <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-bold text-slate-200 sm:text-xs">
                    <Gauge className="size-3 shrink-0 text-indigo-300 sm:size-3.5" strokeWidth={2.25} aria-hidden />
                    کیفیت
                  </span>
                  <label className="relative min-w-0 flex-1 sm:min-w-[7rem] sm:flex-initial">
                    <select
                      value={selectedQuality}
                      onChange={(e) => handleQualityChange(e.target.value)}
                      className="peer h-8 w-full min-w-0 cursor-pointer appearance-none rounded-full border border-white/15 bg-white/10 py-1.5 pe-8 ps-3 text-[10px] font-bold text-white transition hover:border-indigo-300/50 hover:bg-white/[0.16] focus:border-indigo-300/60 focus:outline-none focus:ring-[3px] focus:ring-[#6366f1]/35 sm:h-9 sm:py-2 sm:pe-9 sm:text-xs [&>option]:text-slate-900"
                      aria-label="کیفیت پخش ویدیو"
                    >
                      {QUALITY_OPTIONS.map((q) => (
                        <option key={q.value} value={q.value}>
                          {q.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="pointer-events-none absolute top-1/2 size-3 -translate-y-1/2 text-slate-200 end-2.5 sm:size-3.5 sm:end-3"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </label>
                </div>
              </div>

              <div className="relative isolate min-w-0 bg-black ring-1 ring-inset ring-white/[0.05]">
                {videoUrl ? (
                  <div
                    ref={playerFrameRef}
                    className="fa-fs group relative aspect-video w-full min-w-0 max-w-full overflow-hidden bg-black"
                    onMouseMove={revealControls}
                    onMouseLeave={() => {
                      if (isPlaying) setShowControls(false);
                    }}
                    onTouchStart={revealControls}
                  >
                    <div
                      aria-hidden
                      className="absolute right-0 top-0 z-30 h-9 w-56 cursor-default bg-[#0b1430]"
                      onClick={(e) => e.preventDefault()}
                      onMouseDown={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    <video
                      ref={nativeVideoRef}
                      key={`${videoId}-${selectedQuality}-${videoUrl}`}
                      className="course-stream-video accent-brand-primary absolute inset-0 h-full max-h-full w-full cursor-pointer touch-manipulation bg-black object-contain outline-none [-webkit-tap-highlight-color:transparent]"
                      style={{ direction: 'rtl' }}
                      src={videoUrl}
                      {...(poster ? { poster } : {})}
                      playsInline
                      preload="auto"
                      disablePictureInPicture
                      onClick={togglePlay}
                      onContextMenu={(e) => e.preventDefault()}
                      onMouseDown={(e) => {
                        if (e.button === 2) e.preventDefault();
                      }}
                      onLoadedData={() => {
                        setPlaybackError('');
                        setIsBuffering(false);
                      }}
                      onLoadedMetadata={(ev) => {
                        const el = ev.currentTarget;
                        const rs = resumeAtSec;
                        if (rs != null && rs > 0.35 && Number.isFinite(rs)) {
                          try {
                            el.currentTime = rs;
                            onResumeApplied();
                          } catch {
                            /* noop */
                          }
                        }
                        try {
                          el.playbackRate = playbackRate;
                          el.volume = volume;
                          el.muted = isMuted;
                        } catch {
                          /* noop */
                        }
                        setDuration(Number.isFinite(el.duration) ? el.duration : 0);
                        metadataDurationRef.current = Number.isFinite(el.duration) ? el.duration : 0;
                        if (resumePlayingAfterRefresh) {
                          void el.play().catch(() => undefined);
                          setResumePlayingAfterRefresh(false);
                        }
                      }}
                      onDurationChange={(ev) =>
                        setDuration(Number.isFinite(ev.currentTarget.duration) ? ev.currentTarget.duration : 0)
                      }
                      onTimeUpdate={(ev) => setCurrentTime(ev.currentTarget.currentTime || 0)}
                      onPlay={() => {
                        setIsPlaying(true);
                        revealControls();
                      }}
                      onPause={() => {
                        setIsPlaying(false);
                        setShowControls(true);
                      }}
                      onPlaying={() => setIsBuffering(false)}
                      onWaiting={() => setIsBuffering(true)}
                      onVolumeChange={(ev) => {
                        setVolume(ev.currentTarget.volume);
                        setIsMuted(ev.currentTarget.muted || ev.currentTarget.volume === 0);
                      }}
                      onProgress={(ev) => {
                        try {
                          const b = ev.currentTarget.buffered;
                          const d = ev.currentTarget.duration;
                          if (b.length && d) setBufferedPct(Math.min(100, (b.end(b.length - 1) / d) * 100));
                        } catch {
                          /* noop */
                        }
                      }}
                      onError={(ev) => {
                        const el = ev.currentTarget;
                        const message = formatNativePlaybackError(el);
                        const code = el.error?.code;
                        if (code !== 2 && code !== 4) {
                          setPlaybackError(message);
                          return;
                        }
                        void recoverStream(!el.paused).then((ok) => {
                          if (!ok) setPlaybackError(message);
                        });
                      }}
                      onEnded={(ev) => {
                        // اگر قبل از انتهای واقعی فایل «تمام» شد، اتصال قطع شده؛ ادامه بده نه قسمت بعد
                        const total = metadataDurationRef.current;
                        if (total > 0 && ev.currentTarget.currentTime < total - 5) {
                          void recoverStream(true).then((ok) => {
                            if (!ok) setPlaybackError('اتصال پخش قطع شد؛ صفحه را دوباره بارگذاری کنید.');
                          });
                          return;
                        }
                        if (hasNext) goToNextVideo();
                      }}
                    />

                    {/* اسپینر بافر (فقط هنگام پخش) یا دکمهٔ مرکزی پخش (هنگام توقف) */}
                    {isBuffering && isPlaying ? (
                      <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center">
                        <span className="h-12 w-12 animate-spin rounded-full border-[3px] border-white/25 border-t-white sm:h-14 sm:w-14" />
                      </div>
                    ) : !isPlaying ? (
                      <button
                        type="button"
                        onClick={togglePlay}
                        aria-label="پخش"
                        className="absolute inset-0 z-10 grid place-items-center"
                      >
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#2f5ae0] to-[#6366f1] text-white shadow-[0_12px_34px_-8px_rgba(79,70,229,0.7)] ring-1 ring-white/25 transition-transform hover:scale-105 sm:h-20 sm:w-20">
                          <Play className="size-7 translate-x-[2px] sm:size-8" fill="currentColor" />
                        </span>
                      </button>
                    ) : null}

                    {/* کنترل‌بار پایین */}
                    <div
                      dir="ltr"
                      onClick={revealControls}
                      className={`absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-2.5 pb-2 pt-10 transition-opacity duration-300 sm:px-4 sm:pb-3 ${
                        controlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
                      }`}
                    >
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={0.1}
                        value={progressPct}
                        onChange={onSeekChange}
                        className="fa-video-range w-full"
                        style={{ background: seekBg }}
                        aria-label="نوار پیشرفت ویدیو"
                      />
                      <div className="mt-1 flex items-center gap-1 text-white sm:mt-1.5 sm:gap-2">
                        <button
                          type="button"
                          onClick={togglePlay}
                          aria-label={isPlaying ? 'توقف' : 'پخش'}
                          className="inline-flex size-8 items-center justify-center rounded-full text-white/90 transition hover:bg-white/10 hover:text-white"
                        >
                          {isPlaying ? (
                            <Pause className="size-[18px]" fill="currentColor" />
                          ) : (
                            <Play className="size-[18px] translate-x-px" fill="currentColor" />
                          )}
                        </button>
                        <span className="select-none font-mono text-[11px] tabular-nums text-white/85 sm:text-xs">
                          {fmtTime(currentTime)} / {fmtTime(duration)}
                        </span>
                        <div className="flex-1" />
                        <button
                          type="button"
                          onClick={cyclePlaybackRate}
                          aria-label="سرعت پخش"
                          className="rounded-full px-2 py-1 text-[11px] font-bold text-white/85 transition hover:bg-white/10 hover:text-white"
                        >
                          {playbackRate}x
                        </button>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={toggleMute}
                            aria-label={isMuted ? 'قطع بی‌صدا' : 'بی‌صدا'}
                            className="inline-flex size-8 items-center justify-center rounded-full text-white/90 transition hover:bg-white/10 hover:text-white"
                          >
                            {isMuted ? <VolumeX className="size-[18px]" /> : <Volume2 className="size-[18px]" />}
                          </button>
                          <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.01}
                            value={isMuted ? 0 : volume}
                            onChange={onVolumeSlide}
                            className="fa-video-vol hidden w-16 sm:block"
                            style={{ background: volBg }}
                            aria-label="بلندی صدا"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={toggleFullscreen}
                          aria-label={isFullscreen ? 'خروج از تمام‌صفحه' : 'تمام‌صفحه'}
                          className="inline-flex size-8 items-center justify-center rounded-full text-white/90 transition hover:bg-white/10 hover:text-white"
                        >
                          {isFullscreen ? <Minimize className="size-[18px]" /> : <Maximize className="size-[18px]" />}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex aspect-video items-center justify-center bg-slate-950 px-4 text-center text-xs font-semibold text-slate-300">
                    آدرس پخش در دسترس نیست.
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-[1.25rem] border border-slate-300/60 bg-transparent px-3 py-4 sm:px-6 sm:py-6">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="h-4 w-1 rounded-full bg-[#1C3B9B]" aria-hidden />
                <span className="text-[10px] font-bold tracking-wide text-[#1C3B9B] sm:text-[11px]">درباره این جلسه</span>
              </div>
              <h2 className="text-[0.9375rem] font-black leading-snug tracking-tight text-slate-900 sm:text-xl md:text-2xl">
                {videoInfo.title}
              </h2>
              {videoInfo.description ? (
                <p className="mt-2 text-[13px] leading-relaxed text-[#475569] sm:text-sm">
                  {videoInfo.description}
                </p>
              ) : (
                <p className="mt-2 text-[13px] leading-relaxed text-slate-400 sm:text-sm">
                  توضیحات تکمیلی برای این ویدیو ثبت نشده است.
                </p>
              )}

              {courseVideos.length > 1 ? (
                <div className="mt-5 flex items-center justify-center gap-2 border-t border-slate-200/70 pt-4 sm:gap-3 sm:pt-5">
                  <button
                    type="button"
                    onClick={goToPreviousVideo}
                    disabled={!hasPrevious}
                    className={`inline-flex min-h-[40px] items-center justify-center gap-1 rounded-full px-4 py-2 text-[13px] font-bold transition sm:gap-1.5 sm:text-sm ${
                      hasPrevious
                        ? 'border border-slate-300 bg-white text-slate-700 hover:border-[#1C3B9B]/50 hover:text-[#1C3B9B] active:scale-[0.98]'
                        : 'cursor-not-allowed border border-slate-200 bg-transparent text-slate-400'
                    }`}
                  >
                    <ChevronRight className="size-4 shrink-0" strokeWidth={2.5} aria-hidden />
                    قبلی
                  </button>

                  <span className="min-w-[4.25rem] shrink-0 rounded-full border border-slate-300/70 bg-white px-3 py-1.5 text-center text-[11px] font-bold tabular-nums text-slate-600">
                    {toFaDigits(currentIndex + 1)} از {toFaDigits(Math.max(courseVideos.length, 1))}
                  </span>

                  <button
                    type="button"
                    onClick={goToNextVideo}
                    disabled={!hasNext}
                    className={`inline-flex min-h-[40px] items-center justify-center gap-1 rounded-full px-5 py-2 text-[13px] font-bold transition sm:gap-1.5 sm:text-sm ${
                      hasNext
                        ? 'bg-[#1C3B9B] text-white hover:bg-[#173183] active:scale-[0.98]'
                        : 'cursor-not-allowed bg-slate-100 text-slate-400'
                    }`}
                  >
                    بعدی
                    <ChevronLeft className="size-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <aside className="min-w-0">
            <div className="isolate z-40 overflow-hidden rounded-[1.25rem] border border-slate-300/60 bg-transparent sm:rounded-[1.375rem] lg:sticky lg:top-[calc(7.375rem+env(safe-area-inset-top,0px))]">
              <div className="border-b border-slate-300/50 bg-transparent px-3 py-2.5 sm:px-4 sm:py-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#1C3B9B] text-white sm:size-10">
                    <ListVideo className="size-4 sm:size-[1.125rem]" strokeWidth={2.25} aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1 text-right">
                    <h3 className="text-[13px] font-black tracking-tight text-slate-900 sm:text-[0.95rem]">فهرست ویدیوها</h3>
                    <p className="mt-0.5 text-[9px] text-slate-500 sm:text-[10px]">
                      {toFaDigits(courseVideos.length)} جلسه • برای تعویض انتخاب کنید
                    </p>
                  </div>
                </div>
              </div>
              <div className="fa-media-scroll max-h-[min(40dvh,300px)] space-y-0.5 overflow-y-auto overscroll-contain px-1.5 py-2 sm:max-h-[min(70dvh,520px)] sm:p-3 md:p-4">
                {courseVideos.map((courseVideo, index) => {
                  const isActive = courseVideo.id === videoId;
                  return (
                  <button
                    key={courseVideo.id}
                    type="button"
                    onClick={() => handleVideoSelect(courseVideo.id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={`group relative flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-right transition active:scale-[0.995] sm:px-2.5 sm:py-2 ${
                      isActive
                        ? 'bg-white shadow-[0_6px_18px_-12px_rgba(28,59,155,0.45)] ring-1 ring-[#1C3B9B]/12'
                        : 'bg-transparent hover:bg-white/60'
                    }`}
                  >
                    {isActive ? (
                      <span aria-hidden className="absolute inset-y-2 right-0 w-[3px] rounded-full bg-[#1C3B9B]" />
                    ) : null}
                    {isActive ? (
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#1C3B9B] text-white sm:size-9">
                        <span className="flex h-3.5 items-end gap-[2px]" aria-hidden>
                          {[0, 1, 2].map((i) => (
                            <span
                              key={i}
                              className="fa-list-eq w-[2px] rounded-full bg-white"
                              style={{ animationDelay: `${i * 0.16}s` }}
                            />
                          ))}
                        </span>
                      </div>
                    ) : (
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums text-slate-400 transition group-hover:text-[#1C3B9B] sm:size-9">
                        {toFaDigits(index + 1)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`line-clamp-2 text-[11px] font-bold leading-snug sm:text-[13px] ${isActive ? 'text-[#1C3B9B]' : 'text-slate-600 group-hover:text-slate-900'}`}>
                        {courseVideo.title}
                      </p>
                    </div>
                    {courseVideo.duration ? (
                      <span className={`shrink-0 font-mono text-[10px] tabular-nums sm:text-[11px] ${isActive ? 'text-[#1C3B9B]/80' : 'text-slate-400'}`}>
                        {toFaDigits(Math.floor(courseVideo.duration / 60))}:{toFaDigits(String(Math.floor(courseVideo.duration % 60)).padStart(2, '0'))}
                      </span>
                    ) : null}
                  </button>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerPage;
